import React, { Component } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'

import * as Helpers from './helpers'
import * as StatisticsActions from '../../../actions/statistics'
import * as StoresActions from '../../../actions/stores'
import { routes } from '../../../constants'
import { StatisticsDashboard } from '../../../components/Screens'
import config from '../../../config'
import { statistics as statisticsProps, store as storeProps } from '../../../types'
import { isHeightXXL } from '../../../reducers/app'

const valuesDateFilter = [1, 24, 24 * 7, 24 * 30]

const defaultStore = 'all'
const defaultHours = valuesDateFilter[1]

/**
 * Dashboard container
 */
class Dashboard extends Component {
    static propTypes = {
        clearSelectedStore: PropTypes.func.isRequired,
        data: PropTypes.shape(statisticsProps).isRequired,
        fetchSingleStore: PropTypes.func.isRequired,
        fetchStatistics: PropTypes.func.isRequired,
        fetchStoresPaginated: PropTypes.func.isRequired,
        history: PropTypes.shape({
            replace: PropTypes.func,
        }).isRequired,
        isScreenLarge: PropTypes.bool.isRequired,
        isWorking: PropTypes.bool.isRequired,
        match: PropTypes.shape({
            params: PropTypes.shape({
                hours: PropTypes.string,
                store: PropTypes.string,
            }),
        }).isRequired,
        selectedStore: PropTypes.shape(storeProps),
        stores: PropTypes.arrayOf(PropTypes.shape(storeProps)).isRequired,
        t: PropTypes.func.isRequired,
        selectedEstate: PropTypes.string,
    }

    static defaultProps = {
        selectedStore: {},
        selectedEstate: '',
    }

    constructor(props) {
        super(props)

        this.state = {
            loading: null,
            storeInputValue: '',
        }

        this.onChangeDateFilter = this.onChangeDateFilter.bind(this)
        this.clearFilters = this.clearFilters.bind(this)
        this.onStoreChange = this.onStoreChange.bind(this)
        this.onStoreInputChange = this.onStoreInputChange.bind(this)
    }

    /**
     * React's componentDidMount
     */
    componentDidMount() {
        if (!this.getStoreParam()) {
            this.redirect()
        }

        this.fetchAllData()

        this.interval = setInterval(this.fetchStatistics.bind(this), config.statistics.resfreshTime)
    }

    /**
     * React's componentDidUpdate
     * @param {Object} lastProps
     */
    componentDidUpdate(lastProps) {
        const newHoursParams = this.getHoursParam()
        const lastHoursParams = this.getHoursParam(lastProps)
        const newStoreParams = this.getStoreParam()
        const lastStoreParams = this.getStoreParam(lastProps)

        const { selectedEstate: lastEstate } = lastProps
        const { selectedEstate: nextEstate } = this.props

        if (lastEstate !== nextEstate) {
            this.fetchStores()
            this.clearFilters()
            return
        }

        if (newStoreParams !== lastStoreParams) {
            this.fetchStore()
            this.fetchStatistics()
        } else if (lastHoursParams !== newHoursParams) {
            this.fetchStatistics()
        }

        const { isWorking } = this.props
        const { isWorking: wasWorking } = lastProps

        if (isWorking && !wasWorking) {
            this.initLoadingCounter()
        }

        if (!isWorking && wasWorking) {
            this.stopLoadingCounter()
        }
    }

    /**
     * Clears interval when component is unmounted
     */
    componentWillUnmount() {
        if (this.interval) {
            clearInterval(this.interval)
        }

        if (this.timeoutLoading) {
            clearTimeout(this.timeoutLoading)
        }
    }

    /**
     * Gets all the needed data with the proper transformations applied
     */
    get data() {
        const { data } = this.props
        const {
            activityCount,
            topBrands: currentBrands,
            prevTopBrands: prevBrands,
            topModels: currentModels,
            prevTopModels: prevModels,
            topStores: currentStores,
            prevTopStores: prevStores,
            bottomStores: currentBottomStores,
            prevBottomStores,
        } = data

        // Build activityCount
        const activity = Helpers.buildActivityCount(activityCount)

        // Build topStores
        const topStores = Helpers.buildTopStores(currentStores, prevStores)

        // Build worstStores
        const worstStores = Helpers.buildTopStores(currentBottomStores, prevBottomStores)

        // Build topBrands
        const topBrands = Helpers.buildTopBrands(currentBrands, prevBrands)

        // Build topModels
        const topModels = Helpers.buildTopModels(currentModels, prevModels)

        return {
            ...data,
            activity,
            topBrands,
            topModels,
            topStores,
            worstStores,
        }
    }

    /**
     * Gets stores list formatted for suggestions input
     */
    get stores() {
        const { stores } = this.props
        const formatted = stores.map(({ code, name }) => ({
            key: code,
            value: name,
        }))

        return formatted
    }

    /**
     * Is clear filters visible
     */
    get isClearFiltersVisible() {
        const storeParam = this.getStoreParam()
        const hoursParam = this.getHoursParam()

        return (storeParam && storeParam !== defaultStore) || (hoursParam && hoursParam !== defaultHours)
    }

    /**
     * Initializes a progressive counter when loading
     */
    initLoadingCounter() {
        this.setState({ loading: 0 })
    }

    /**
     * Initializes a progressive counter when loading
     */
    stopLoadingCounter() {
        const clear = () => this.setState({ loading: null })
        this.setState({ loading: 1 }, () => {
            this.timeoutLoading = setTimeout(clear.bind(this), 1000)
        })
    }

    /**
     * Gets the `store` parameter from some specified props,
     * or uses the ones that belong to this instance by default
     * @param {Object} props
     */
    getStoreParam(props) {
        const {
            match: {
                params: { store },
            },
        } = props || this.props

        return store === defaultStore ? '' : store
    }

    /**
     * Gets the `hours` parameter from some specified props,
     * or uses the ones that belong to this instance by default
     * @param {Object} props
     */
    getHoursParam(props) {
        const {
            match: {
                params: { hours },
            },
        } = props || this.props

        return parseInt(hours, 10) || valuesDateFilter[1]
    }

    /**
     * Callback on change date filter
     */
    onChangeDateFilter(hours) {
        this.redirect({ hours })
    }

    /**
     * Callback on click on store list
     * @param {String} store Store code
     */
    onStoreChange(store) {
        this.redirect({ store })
    }

    /**
     * Callback on store name input changes
     * @param {String} value Partial or total store name
     */
    onStoreInputChange(value) {
        this.setState({ storeInputValue: value }, () => {
            if (value.length > 2 || value.length === 0) {
                this.fetchStores()
            }
        })
    }

    clearFilters() {
        const { clearSelectedStore } = this.props
        this.redirect({ store: defaultStore, hours: defaultHours })
        clearSelectedStore()
    }

    /**
     * Non-blocking call to all fetch methods
     */
    fetchAllData() {
        this.fetchStore()
        this.fetchStatistics()
        this.fetchStores()
    }

    /**
     * Fetche store from store code url param
     */
    async fetchStore() {
        const { fetchSingleStore } = this.props
        const code = this.getStoreParam()

        if (code && code !== defaultStore) {
            const action = await fetchSingleStore({ storeCode: code })
            const { payload } = action || {}
            const { name } = payload || {}
            this.setState({ storeInputValue: name })
        }
    }

    /**
     * Fetches stores from api
     */
    async fetchStores() {
        const { fetchStoresPaginated } = this.props
        const { storeInputValue } = this.state
        await fetchStoresPaginated({
            name: storeInputValue,
            page: 0,
            order: 'name',
        })
    }

    /**
     * Fetches statistics from api
     */
    async fetchStatistics() {
        const { fetchStatistics } = this.props
        const hours = this.getHoursParam()
        const store = this.getStoreParam()
        const params = { hours }

        if (store || store !== defaultStore) {
            params.storeCode = store
        }

        await fetchStatistics(params)
    }

    /**
     * It redirects the page to the proper url.
     * If `params.hours` is null or undefined, it takes the last value
     * from the current url.
     * If `params.store` is null or undefined, it takes the last value
     * from the current url,or the default parameter `all` if that's
     * undefined too.
     * @param {Object} params
     * @param {Number} params.hours New hours parameter
     * @param {String} params.store New store code parameter
     */
    redirect({ hours, store } = {}) {
        const { history } = this.props
        const storeParam = store || this.getStoreParam() || defaultStore || ''
        const hoursParam = hours || this.getHoursParam() || ''

        if (storeParam === defaultStore) {
            this.setState({ storeInputValue: '' })
        }

        history.replace(`${routes.STATISTICS}/${storeParam}/${hoursParam}`)
    }

    render() {
        const { isScreenLarge, selectedStore, t } = this.props
        const { loading, storeInputValue } = this.state
        const storeCodeParam = this.getStoreParam()
        const { name: storeName = defaultStore } = selectedStore || {}

        return (
            <StatisticsDashboard
                data={this.data}
                isClearFiltersVisible={this.isClearFiltersVisible}
                isScreenLarge={isScreenLarge}
                labelsDateFilter={[t('Last Hour'), t('24 Hours'), t('Week'), t('Month')]}
                loading={loading}
                onChangeDateFilter={this.onChangeDateFilter}
                onClearFilters={this.clearFilters}
                onStoreChange={this.onStoreChange}
                onStoreInputChange={this.onStoreInputChange}
                selectedDateFilter={this.getHoursParam()}
                selectedStore={storeCodeParam === defaultStore ? defaultStore : storeName}
                stores={this.stores}
                storeInputValue={storeInputValue}
                valuesDateFilter={valuesDateFilter}
            />
        )
    }
}

const mapStateToProps = ({ app: { window, selectedEstate }, statistics: { data, isWorking }, stores: { list: stores, selectedStore } }) => ({
    data,
    isScreenLarge: isHeightXXL(window),
    isWorking,
    selectedStore,
    stores,
    selectedEstate,
})

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            ...StatisticsActions,
            ...StoresActions,
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Dashboard))
